import _ from 'lodash';
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import Select from '@material-ui/core/Select';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/Button';
import FMPTextField from '../ui/FMPTextField';
import FMPTextFieldNoGrid from '../ui/FMPTextFieldNoGrid';
import DeleteIcon from '@material-ui/icons/Delete';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Link } from 'react-router-dom';
import Note from '../ui/Note';

import { withStyles } from '@material-ui/core/styles/index';

const MHX_TERMINAL_ID = 31;
const PETRO_DIAMOND_TERMINAL_ID = 9;

//- One loading number state field per stop; index 0 is the first stop,
//- the rest line up with the splitLegs prop (stops 2-4)
const LEG_LOADING_FIELDS = [
	'loadingNumber',
	'secondaryLoadingNumber',
	'tertiaryLoadingNumber',
	'quaternaryLoadingNumber',
];

const styles = () => {
	return {
		addInputButton: {
			borderRadius: '100px',
			marginTop: 20,
		},
		supplierDetail: {
			margin: 5,
			color: '#999',
			fontSize: 13,
			fontStyle: 'italic',
		},
	};
};

const formFields = {
	backups: [
		{
			supplierId: '',
			loadingNumber: '',
			accountNumber: '',
		},
	],
};

function generateMhxLoadingNumber() {
	return Math.floor(100000 + Math.random() * 900000).toString();
}

class DeliveryTerminalSupplierPicker extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			terminalId: '',
			supplierId: '',
			loadingNumber: '',
			secondaryLoadingNumber: '',
			tertiaryLoadingNumber: '',
			quaternaryLoadingNumber: '',
			stationNumber: '',
			splitLegCount: 0,
			accountNumber: '',
			emailCopyOfDeliveryRequest: '',
			useStationDeliveryIdAsLoadingNumber: '',
			requireLoadingNumberPerStationOnSplitLoads: '',
			useLoadingAssignments: '',
			useAssignedLoadingNumbers: false,
			assignedLookup: null,
			generatedValues: {},
			carrierNeedsToProvidePickupWindow: '',
			backups: [],
			supplierOptions: [],
		};

		//- Values we wrote into the loading number fields ourselves (lookup or generator).
		//- A field holding one of these is safe to overwrite; anything else was typed by a person.
		this.autoFilled = {};
		this.lastAssignedLookupKey = null;
	}

	componentDidMount() {
		this.maybeLookupAssigned();
	}

	componentWillUnmount() {
		this.isUnmounted = true;
	}

	//- the loading number fields for the stops currently on the delivery
	getSplitLegFields(props = this.props) {
		const splitLegs = props.splitLegs || [];
		return splitLegs.map((leg, i) => ({ leg, fieldName: LEG_LOADING_FIELDS[i + 1] }));
	}

	componentDidUpdate(prevProps) {
		this.maybeLookupAssigned();
		const curProps = this.props;
		const { terminalId, requireLoadingNumberPerStationOnSplitLoads } = this.state;

		const prevLegCount = (prevProps.splitLegs || []).length;
		const curLegCount = (curProps.splitLegs || []).length;

		if (curLegCount > prevLegCount || (curProps.isSplitLoad && !prevProps.isSplitLoad)) {
			//- a stop was added: MHX gets a generated number for each stop missing one
			if (terminalId === MHX_TERMINAL_ID) {
				const newState = {};
				this.getSplitLegFields().forEach(({ fieldName }) => {
					if (!this.state[fieldName]) {
						newState[fieldName] = generateMhxLoadingNumber();
						this.autoFilled[fieldName] = newState[fieldName];
					}
				});
				if (_.keys(newState).length) {
					this.setState(newState);
					this.callOnChange(newState);
				}
			}

			if (terminalId === PETRO_DIAMOND_TERMINAL_ID && !requireLoadingNumberPerStationOnSplitLoads) {
				const newState = { requireLoadingNumberPerStationOnSplitLoads: true };
				this.setState(newState);
				this.callOnChange(newState);
			}
		} else if (curLegCount < prevLegCount) {
			//- stops were removed: clear the loading numbers past the last stop
			const newState = {};
			for (let i = curLegCount + 1; i < LEG_LOADING_FIELDS.length; i++) {
				if (this.state[LEG_LOADING_FIELDS[i]]) {
					newState[LEG_LOADING_FIELDS[i]] = '';
				}
			}
			if (_.keys(newState).length) {
				this.setState(newState);
				this.callOnChange(newState);
			}
		}
	}

	refreshGeneratedLoadingNumber = (fieldName) => {
		const { terminalId } = this.state;
		let nextValue = '';
		if (terminalId === MHX_TERMINAL_ID) {
			nextValue = generateMhxLoadingNumber();
		}

		const newState = { [fieldName]: nextValue };
		this.autoFilled[fieldName] = nextValue;
		this.setState(newState);
		this.callOnChange(newState);
	};

	//- Assigned loading numbers (per terminal + station + fuel type) -------------------------

	isAutoValue(fieldName, value) {
		if (!value) {
			return true;
		}
		const generated = _.get(this.state, ['generatedValues', fieldName]);
		return value === this.autoFilled[fieldName] || value === generated;
	}

	getAssignedLookupKey() {
		const { terminalId, supplierId, useAssignedLoadingNumbers, requireLoadingNumberPerStationOnSplitLoads } =
			this.state;
		const { stationId, splitLegs, productTypes, isSplitLoad } = this.props;
		return [
			useAssignedLoadingNumbers ? 1 : 0,
			terminalId,
			supplierId,
			stationId,
			(splitLegs || []).map((leg) => leg.stationId).join(','),
			_.first(productTypes),
			isSplitLoad ? 1 : 0,
			requireLoadingNumberPerStationOnSplitLoads ? 1 : 0,
		].join('|');
	}

	maybeLookupAssigned() {
		const key = this.getAssignedLookupKey();
		if (key === this.lastAssignedLookupKey) {
			return;
		}
		this.lastAssignedLookupKey = key;
		if (!this.state.useAssignedLoadingNumbers) {
			if (this.state.assignedLookup) {
				this.setState({ assignedLookup: null });
			}
			return;
		}
		this.runAssignedLookup();
	}

	runAssignedLookup = () => {
		const { lookupAssignedLoadingNumbers, stationId, productTypes, isSplitLoad } = this.props;
		const { terminalId, requireLoadingNumberPerStationOnSplitLoads, useAssignedLoadingNumbers } =
			this.state;
		const productType = _.first(productTypes);

		if (
			!useAssignedLoadingNumbers ||
			!_.isFunction(lookupAssignedLoadingNumbers) ||
			!terminalId ||
			!stationId ||
			!productType
		) {
			return;
		}

		const needsPerLeg =
			!!isSplitLoad &&
			(!!requireLoadingNumberPerStationOnSplitLoads || terminalId === PETRO_DIAMOND_TERMINAL_ID);
		const legFields = this.getSplitLegFields();
		const legStationIds = legFields.map(({ leg }) => leg.stationId || stationId);
		const stationIds = _.uniq(_.compact([stationId, ...(needsPerLeg ? legStationIds : [])]));
		const requestKey = this.getAssignedLookupKey();

		lookupAssignedLoadingNumbers({
			params: { terminalId, productType, stationIds },
			callback: (err, r) => {
				if (err || !r || this.isUnmounted) {
					return;
				}
				//- Something changed while we were waiting, that change triggered its own lookup
				if (requestKey !== this.getAssignedLookupKey()) {
					return;
				}

				const byStation = _.keyBy(r.results, 'stationId');
				const stationMap = _.keyBy(_.get(r, ['collections', 'stationCollection'], []), 'stationId');
				const terminal = _.first(_.get(r, ['collections', 'terminalCollection'], []));

				const newState = {};
				const missing = [];
				const found = {};
				const apply = (fieldName, sid) => {
					const result = byStation[sid];
					if (result && result.found) {
						found[fieldName] = { stationId: sid, stationNumber: _.get(stationMap, [sid, 'number'], '') };
						if (this.isAutoValue(fieldName, this.state[fieldName])) {
							newState[fieldName] = result.loadingNumber;
							this.autoFilled[fieldName] = result.loadingNumber;
						}
					} else {
						missing.push({
							stationId: sid,
							stationNumber: _.get(stationMap, [sid, 'number'], `#${sid}`),
							stationName: _.get(stationMap, [sid, 'name'], ''),
						});
					}
				};

				apply('loadingNumber', stationId);
				if (needsPerLeg) {
					legFields.forEach(({ leg, fieldName }) => {
						apply(fieldName, leg.stationId || stationId);
					});
				}

				this.setState({
					assignedLookup: {
						terminalId,
						terminalName: terminal ? terminal.name : '',
						fuelTypeLabel: r.fuelTypeLabel || productType,
						missing,
						found,
					},
				});
				if (_.keys(newState).length) {
					this.callOnChange(newState);
				}
			},
		});
	};

	// noinspection JSUnusedGlobalSymbols
	static getDerivedStateFromProps(nextProps, prevState) {
		const { terminals, supplierMap, productTypes, onChange } = nextProps;
		const {
			terminalId,
			supplierId,
			isSplitLoad,
			stationNumber,
			splitLegs,
			accountNumber,
			emailCopyOfDeliveryRequest,
			useStationDeliveryIdAsLoadingNumber,
			carrierNeedsToProvidePickupWindow,
			requireLoadingNumberPerStationOnSplitLoads,
			useLoadingAssignments,
			useAssignedLoadingNumbers,
			backups,
		} = nextProps;

		const legCount = (splitLegs || []).length;
		const extraLegFields = LEG_LOADING_FIELDS.slice(1, 1 + legCount);

		let { loadingNumber } = nextProps;
		const extraLegNumbers = {};
		extraLegFields.forEach((fieldName) => {
			extraLegNumbers[fieldName] = nextProps[fieldName];
		});
		const generatedValues = {};

		//- No backups? Clear them
		let newState = {};

		if (
			terminalId === prevState.terminalId &&
			supplierId === prevState.supplierId &&
			backups === prevState.backups &&
			stationNumber === prevState.stationNumber &&
			legCount === prevState.splitLegCount
		) {
			return newState;
		}

		if (!terminalId) {
			newState.terminalId = '';
			return newState;
		}

		const stateLoadingNumber = prevState.loadingNumber;
		if (terminalId !== prevState.terminalId && terminalId === MHX_TERMINAL_ID && !loadingNumber) {
			loadingNumber = generatedValues.loadingNumber = generateMhxLoadingNumber();
		}

		if (terminalId === MHX_TERMINAL_ID && isSplitLoad) {
			extraLegFields.forEach((fieldName) => {
				if (!extraLegNumbers[fieldName]) {
					extraLegNumbers[fieldName] = generatedValues[fieldName] = generateMhxLoadingNumber();
				}
			});
		}

		let isLoadingNumberGenerated = false;
		if (terminalId === MHX_TERMINAL_ID) {
			isLoadingNumberGenerated = true;
		}

		//- Either we just mounted or we got a new prop passed in
		//- so lets reset the state
		newState = {
			terminalId,
			supplierId: '',
			//loadingNumber,
			loadingNumber: stateLoadingNumber || loadingNumber,
			stationNumber,
			splitLegCount: legCount,
			isLoadingNumberGenerated,
			generatedValues,
			useAssignedLoadingNumbers: false,
			accountNumber: '',
			emailCopyOfDeliveryRequest: false,
			useStationDeliveryIdAsLoadingNumber: false,
			carrierNeedsToProvidePickupWindow: false,
			backups: [],
			supplierOptions: [],
		};
		LEG_LOADING_FIELDS.slice(1).forEach((fieldName) => {
			newState[fieldName] = prevState[fieldName] || extraLegNumbers[fieldName] || '';
		});
		const terminal = _.find(terminals, { terminalId });
		if (!terminal) {
			console.error('Missing terminal', terminals, terminalId);
			return;
		}

		const supplierOptions = getSupplierOptions({
			terminal,
			supplierMap,
			productTypes,
		});
		newState.supplierOptions = supplierOptions;

		//- Populate loading / account numbers
		if (supplierId) {
			newState.supplierId = supplierId;
			const supplierData = _.find(supplierOptions, { supplierId });

			if (supplierData) {
				newState.loadingNumber = supplierData.loadingNumber;
				newState.secondaryLoadingNumber = supplierData.secondaryLoadingNumber;
				newState.accountNumber = supplierData.accountNumber;
				newState.emailCopyOfDeliveryRequest = supplierData.emailCopyOfDeliveryRequest;
				newState.useStationDeliveryIdAsLoadingNumber =
					supplierData.useStationDeliveryIdAsLoadingNumber;
				newState.requireLoadingNumberPerStationOnSplitLoads =
					supplierData.requireLoadingNumberPerStationOnSplitLoads;
				newState.useLoadingAssignments = supplierData.useLoadingAssignments;
				newState.useAssignedLoadingNumbers = !!supplierData.useAssignedLoadingNumbers;
				newState.carrierNeedsToProvidePickupWindow = supplierData.carrierNeedsToProvidePickupWindow;

				if (supplierData.useStationDeliveryIdAsLoadingNumber) {
					LEG_LOADING_FIELDS.forEach((fieldName) => {
						newState[fieldName] = '';
					});
				}
			}
		}

		if (loadingNumber) {
			newState.loadingNumber = loadingNumber;
		}
		extraLegFields.forEach((fieldName) => {
			if (extraLegNumbers[fieldName]) {
				newState[fieldName] = extraLegNumbers[fieldName];
			}
		});

		if (accountNumber) {
			newState.accountNumber = accountNumber;
		}

		if (emailCopyOfDeliveryRequest) {
			newState.emailCopyOfDeliveryRequest = emailCopyOfDeliveryRequest;
		}

		if (useStationDeliveryIdAsLoadingNumber) {
			newState.useStationDeliveryIdAsLoadingNumber = useStationDeliveryIdAsLoadingNumber;
		}
		if (requireLoadingNumberPerStationOnSplitLoads) {
			newState.requireLoadingNumberPerStationOnSplitLoads = requireLoadingNumberPerStationOnSplitLoads;
		}
		if (terminalId === PETRO_DIAMOND_TERMINAL_ID && isSplitLoad) {
			newState.requireLoadingNumberPerStationOnSplitLoads = true;
		}

		if (useLoadingAssignments) {
			newState.useLoadingAssignments = useLoadingAssignments;
		}
		if (useAssignedLoadingNumbers) {
			newState.useAssignedLoadingNumbers = true;
		}

		if (carrierNeedsToProvidePickupWindow) {
			newState.carrierNeedsToProvidePickupWindow = carrierNeedsToProvidePickupWindow;
		}

		//- Populate loading / account numbers PER backup
		if (backups && backups.length) {
			//backups.forEach(backup => {
			//	const supplierId = backup.supplierId;
			//	const supplierData = _.find(supplierOptions, { supplierId });
			//	backup.loadingNumber = supplierData.loadingNumber;
			//	backup.accountNumber = supplierData.accountNumber;
			//});
			newState.backups = backups;
		}

		onChange(newState);
		return newState;
	}

	onSupplierChange = (event, index) => {
		const { stationDeliveryId } = this.props;
		const supplierId = event.target.value;
		const { supplierOptions } = this.state;
		const option = _.find(supplierOptions, { supplierId });

		// Keep existing loading number
		const stateLoadingNumber = this.state.loadingNumber;
		const stateSecondaryLoadingNumber = this.state.secondaryLoadingNumber;
		const {
			loadingNumber = '',
			secondaryLoadingNumber = '',
			accountNumber = '',
			emailCopyOfDeliveryRequest = false,
			useStationDeliveryIdAsLoadingNumber = false,
			requireLoadingNumberPerStationOnSplitLoads = false,
			useLoadingAssignments = false,
			useAssignedLoadingNumbers = false,
			carrierNeedsToProvidePickupWindow = false,
		} = option;

		const isPetroDiamondSplit =
			this.state.terminalId === PETRO_DIAMOND_TERMINAL_ID && this.props.isSplitLoad;

		let newState = {};
		if (!_.isNumber(index)) {
			newState = {
				supplierId,
				loadingNumber: stateLoadingNumber || loadingNumber,
				secondaryLoadingNumber: stateSecondaryLoadingNumber || secondaryLoadingNumber,
				accountNumber,
				emailCopyOfDeliveryRequest,
				useStationDeliveryIdAsLoadingNumber,
				requireLoadingNumberPerStationOnSplitLoads: isPetroDiamondSplit
					? true
					: requireLoadingNumberPerStationOnSplitLoads,
				useLoadingAssignments,
				useAssignedLoadingNumbers: !!useAssignedLoadingNumbers,
				carrierNeedsToProvidePickupWindow,
			};
			if (useStationDeliveryIdAsLoadingNumber && stationDeliveryId) {
				newState.loadingNumber = stationDeliveryId;
				this.getSplitLegFields().forEach(({ fieldName }) => {
					newState[fieldName] = stationDeliveryId;
				});
			}
		} else {
			const backups = this.state.backups.slice();
			backups[index].supplierId = supplierId;
			backups[index].loadingNumber = loadingNumber;
			backups[index].accountNumber = accountNumber;
			newState.backups = backups;
		}
		this.callOnChange(newState);
	};

	onTerminalChange = (event) => {
		const terminalId = event.target.value;
		const { terminals, supplierMap, productTypes, isSplitLoad } = this.props;
		const terminal = _.find(terminals, { terminalId });
		const supplierOptions = getSupplierOptions({
			terminal,
			supplierMap,
			productTypes,
		});

		let loadingNumber = '';
		const extraLegValues = {};
		LEG_LOADING_FIELDS.slice(1).forEach((fieldName) => {
			extraLegValues[fieldName] = '';
		});
		let isLoadingNumberGenerated = false;
		if (terminalId === MHX_TERMINAL_ID) {
			isLoadingNumberGenerated = true;
			loadingNumber = generateMhxLoadingNumber();
			if (isSplitLoad) {
				this.getSplitLegFields().forEach(({ fieldName }) => {
					extraLegValues[fieldName] = generateMhxLoadingNumber();
				});
			}
		}
		this.autoFilled = { loadingNumber, ...extraLegValues };
		const newState = {
			terminalId,
			supplierOptions,
			supplierId: '',
			loadingNumber,
			...extraLegValues,
			stationNumber: this.props.stationNumber,
			accountNumber: '',
			emailCopyOfDeliveryRequest: false,
			useStationDeliveryIdAsLoadingNumber: false,
			carrierNeedsToProvidePickupWindow: false,
			requireLoadingNumberPerStationOnSplitLoads: terminalId === PETRO_DIAMOND_TERMINAL_ID && isSplitLoad,
			useLoadingAssignments: false,
			useAssignedLoadingNumbers: false,
			assignedLookup: null,
			backups: [],
			isLoadingNumberGenerated,
		};
		this.callOnChange(newState);
	};

	onChange(event, fieldName, index, attr) {
		const newState = {};
		if (!_.isNumber(index)) {
			newState[fieldName] = event.target.value;
		} else {
			const inputs = this.state[fieldName].slice();
			if (attr) {
				inputs[index][attr] = event.target.value;
			} else {
				inputs[index] = event.target.value;
			}
			newState[fieldName] = inputs;
		}
		this.callOnChange(newState);
	}

	callOnChange = (newState) => {
		this.props.onChange(newState);
		this.setState(newState);
	};

	addInput = (arrayName) => {
		const val = _.cloneDeep(formFields[arrayName]);
		const newState = {
			[arrayName]: this.state[arrayName].concat(val),
		};
		this.callOnChange(newState);
	};

	removeInput = (arrayName, index) => {
		const inputs = this.state[arrayName].slice();
		inputs.splice(index, 1);
		const newState = { [arrayName]: inputs };
		this.callOnChange(newState);
	};

	render() {
		const {
			error,
			label,
			terminals,
			classes,
			isSplitLoad,
			productTypes,
			stationDeliveryId,
			stationNumber,
			stationName,
			splitLegs,
		} = this.props;
		const {
			terminalId,
			supplierId,
			emailCopyOfDeliveryRequest,
			useStationDeliveryIdAsLoadingNumber,
			carrierNeedsToProvidePickupWindow,
			requireLoadingNumberPerStationOnSplitLoads,
			useLoadingAssignments,
			useAssignedLoadingNumbers,
			assignedLookup,
			backups,
			supplierOptions,
			isLoadingNumberGenerated,
		} = this.state;

		const disabled = !supplierOptions || !supplierOptions.length;

		let showTwo = false;
		if (isSplitLoad && requireLoadingNumberPerStationOnSplitLoads) {
			showTwo = requireLoadingNumberPerStationOnSplitLoads;
		}

		const isPetroDiamondSplit = isSplitLoad && terminalId === PETRO_DIAMOND_TERMINAL_ID;
		if (isPetroDiamondSplit) {
			showTwo = true;
		}

		let loadingHelpText = '';
		if (useStationDeliveryIdAsLoadingNumber) {
			loadingHelpText = `✔ Use Pearson Delivery ID`;
		}

		let loadingNumberDisabled = disabled;

		//- Field stays editable so dispatch can enter / override a loading number they know,
		//- the backend only auto-assigns the next available number when the field is left blank
		if (useLoadingAssignments) {
			loadingHelpText = `✔ Next available FMP loading number used when left blank`;
		}

		if (isLoadingNumberGenerated) {
			if (terminalId === MHX_TERMINAL_ID) {
				loadingHelpText = `✔ Generated automatically for MHX`;
			} else {
				loadingHelpText = `✔ Generated automatically`;
			}
		}

		//- Assigned numbers are fixed per station + fuel type, refreshing can never change them
		const showRefreshIcon = isLoadingNumberGenerated && !useAssignedLoadingNumbers;

		//- One loading number field per stop: the first stop then the split legs
		const legFieldDescriptors = [
			{
				legNumber: 1,
				fieldName: 'loadingNumber',
				stationNumber,
				stationName,
				helpText: loadingHelpText,
			},
			...(splitLegs || []).map((leg, i) => ({
				legNumber: leg.legNumber || i + 2,
				fieldName: LEG_LOADING_FIELDS[i + 1],
				stationNumber: leg.stationNumber,
				stationName: leg.stationName,
				helpText: loadingHelpText,
			})),
		];

		let assignedWarning = null;
		if (useAssignedLoadingNumbers) {
			const fuelLabel = _.get(assignedLookup, 'fuelTypeLabel', '');
			const helpFor = (fieldName) => {
				const found = _.get(assignedLookup, ['found', fieldName]);
				if (found) {
					return `✔ Assigned loading number for ${found.stationNumber || 'station'}${
						fuelLabel ? ` (${fuelLabel})` : ''
					}`;
				}
				if (assignedLookup) {
					return `No assigned loading number on file, enter one manually`;
				}
				return `Looking up the station's assigned loading number...`;
			};
			legFieldDescriptors.forEach((descriptor) => {
				descriptor.helpText = helpFor(descriptor.fieldName);
			});
			loadingHelpText = legFieldDescriptors[0].helpText;

			const missing = _.get(assignedLookup, 'missing', []);
			if (missing.length) {
				const terminalName = assignedLookup.terminalName || 'this terminal';
				assignedWarning = (
					<Note type="warn">
						{missing.map((m) => (
							<div key={m.stationId}>
								No loading number found for <strong>{m.stationNumber}</strong>
								{m.stationName ? ` (${m.stationName})` : ''} at <strong>{terminalName}</strong>
								{fuelLabel ? ` for ${fuelLabel}` : ''}.
							</div>
						))}
						<div style={{ marginTop: 8 }}>
							<Link to={`/logistics/loadingAssignments/assigned/${assignedLookup.terminalId}`}>
								Manage assigned loading numbers for {terminalName}
							</Link>
						</div>
					</Note>
				);
			}
		}

		if (isPetroDiamondSplit) {
			legFieldDescriptors.forEach((descriptor) => {
				const stopLabel = descriptor.legNumber === 1 ? 'Primary station' : `Station #${descriptor.legNumber}`;
				descriptor.helpText = `${stopLabel}: ${descriptor.stationNumber || '—'}${
					descriptor.stationName ? ` (${descriptor.stationName})` : ''
				}${
					useAssignedLoadingNumbers && _.get(assignedLookup, ['found', descriptor.fieldName])
						? ' ✔ assigned'
						: ''
				}`;
			});
		}

		//stationDeliveryId means its an update
		if (useStationDeliveryIdAsLoadingNumber && !stationDeliveryId) {
			loadingNumberDisabled = true;
		}

		const productTypeMap = _.keyBy(productTypes);
		let terminalsWithProduct = _.filter(terminals, (terminal) => {
			if (!terminal.supplierProducts) {
				return false;
			}
			return _.some(terminal.supplierProducts, (sp) => {
				return productTypeMap[sp.product];
			});
		});

		terminalsWithProduct = _.sortBy(terminalsWithProduct, 'name');
		return (
			<div>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<FormControl fullWidth={true} error={!!error}>
							<InputLabel>{label}</InputLabel>
							<Select
								value={terminalId}
								onChange={(event) => this.onTerminalChange(event)}
								input={<Input />}
							>
								{terminalsWithProduct.map((terminal) => {
									const { terminalId, name } = terminal;
									return (
										<MenuItem key={terminalId} value={terminalId}>
											{name}
										</MenuItem>
									);
								})}
							</Select>
							{error && <FormHelperText>{error}</FormHelperText>}
						</FormControl>
					</Grid>
				</Grid>

				<Grid container spacing={2}>
					<Grid item xs={12} md={4}>
						{this.renderSupplierOptions({
							supplierOptions,
							label: 'Primary Supplier',
							supplierId,
							disabled,
						})}

						{emailCopyOfDeliveryRequest && (
							<div className={classes.supplierDetail}>
								<span style={{ opacity: 0.8 }}>✔</span>️ Supplier will receive fuel pickup email
							</div>
						)}

						{carrierNeedsToProvidePickupWindow && (
							<div className={classes.supplierDetail}>
								<span style={{ opacity: 0.8 }}>✔</span>️ Carrier will enter pickup window
							</div>
						)}

						{showTwo && (
							<div className={classes.supplierDetail}>
								<span style={{ opacity: 0.8 }}>✔</span>️ Require a loading # per station on split
								load
							</div>
						)}
					</Grid>

					{showTwo && (
						<Fragment>
							{legFieldDescriptors.map((descriptor) => (
								<Grid key={descriptor.fieldName} item xs={12} md={4} style={{ display: 'flex' }}>
									<FMPTextFieldNoGrid
										name={descriptor.fieldName}
										label={
											isPetroDiamondSplit
												? `Loading #${descriptor.legNumber}`
												: `Station ${descriptor.legNumber} Loading Number`
										}
										controller={this}
										disabled={loadingNumberDisabled}
										contained={false}
										helpText={descriptor.helpText}
									/>
									{showRefreshIcon && (
										<IconButton
											aria-label="refresh loading #"
											onClick={() => this.refreshGeneratedLoadingNumber(descriptor.fieldName)}
											disabled={loadingNumberDisabled}
										>
											<RefreshIcon />
										</IconButton>
									)}
								</Grid>
							))}
						</Fragment>
					)}

					{!showTwo && (
						<Fragment>
							<Grid item xs={12} md={4} style={{ display: 'flex' }}>
								<FMPTextFieldNoGrid
									name={'loadingNumber'}
									label={'Loading Number'}
									controller={this}
									disabled={loadingNumberDisabled}
									contained={false}
									helpText={loadingHelpText}
								/>

								{showRefreshIcon && (
									<IconButton
										aria-label="refresh loading #"
										onClick={() => this.refreshGeneratedLoadingNumber('loadingNumber')}
										disabled={loadingNumberDisabled}
									>
										<RefreshIcon />
									</IconButton>
								)}
							</Grid>

							<FMPTextField
								name={'accountNumber'}
								label={'Account Number'}
								controller={this}
								md={3}
								disabled={disabled}
								contained={false}
							/>
						</Fragment>
					)}
				</Grid>

				{assignedWarning}

				{backups &&
					backups.map((backup, i) => {
						return (
							<Grid container spacing={2} key={i}>
								<Grid item xs={12} md={5}>
									{this.renderSupplierOptions({
										supplierOptions,
										label: 'Backup Supplier',
										supplierId: backup.supplierId,
										index: i,
										disabled,
									})}
								</Grid>

								<FMPTextField
									name={'backups'}
									index={i}
									attr={'loadingNumber'}
									label={'Loading Number'}
									controller={this}
									md={3}
									disabled={disabled}
									contained={false}
								/>

								<FMPTextField
									name={'backups'}
									index={i}
									attr={'accountNumber'}
									label={'Account Number'}
									controller={this}
									md={3}
									disabled={disabled}
									contained={false}
								/>

								<Grid item xs={12} md={1}>
									<IconButton
										color="primary"
										aria-label="Delete"
										onClick={() => this.removeInput('backups', i)}
									>
										<DeleteIcon />
									</IconButton>
								</Grid>
							</Grid>
						);
					})}

				<Grid container spacing={2}>
					<Grid item xs={12} md={5}>
						<Button
							disabled={disabled}
							variant="contained"
							className={classes.addInputButton}
							onClick={() => this.addInput('backups')}
						>
							ADD A BACKUP SUPPLIER
						</Button>
					</Grid>
				</Grid>
			</div>
		);
	}

	renderSupplierOptions({ supplierOptions, supplierId, index, disabled, label }) {
		const styles = {
			product: {
				paddingLeft: 10,
				fontSize: 14,
				color: '#666',
			},
			loading: {
				fontSize: 11,
				lineHeight: '11px',
				color: '#777',
			},
		};

		return (
			<FormControl fullWidth={true} disabled={disabled}>
				<InputLabel>{label}</InputLabel>
				<Select
					value={supplierId}
					onChange={(event) => this.onSupplierChange(event, index)}
					input={<Input />}
				>
					{supplierOptions.map((option) => {
						const { supplierId, name, product } = option;
						return (
							<MenuItem key={supplierId} value={supplierId}>
								<span>{name}</span>
								<span style={styles.product}>{product}</span>
							</MenuItem>
						);
					})}
				</Select>
			</FormControl>
		);
	}
}

function getSupplierOptions({ terminal, supplierMap, productTypes }) {
	const productTypeMap = _.keyBy(productTypes);
	const supplierOptions = [];
	terminal.supplierProducts.forEach((sp) => {
		const { supplierId, product } = sp;
		const supplier = supplierMap[supplierId];
		sp.name = supplier.name;

		if (!supplier) {
			return console.warn('Missing supplier', sp);
		}
		if (!productTypeMap[product]) {
			return;
		}
		supplierOptions.push(sp);
	});
	return supplierOptions;
}

DeliveryTerminalSupplierPicker.propTypes = {
	label: PropTypes.string,
	error: PropTypes.string,
	isSplitLoad: PropTypes.bool,
	terminals: PropTypes.array,
	supplierMap: PropTypes.object,
	productTypes: PropTypes.array,
	terminalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	supplierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	loadingNumber: PropTypes.string,
	secondaryLoadingNumber: PropTypes.string,
	tertiaryLoadingNumber: PropTypes.string,
	quaternaryLoadingNumber: PropTypes.string,
	stationNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	stationName: PropTypes.string,
	//- the stops past the first: { legNumber, stationId, stationNumber, stationName }
	splitLegs: PropTypes.array,
	accountNumber: PropTypes.string,
	emailCopyOfDeliveryRequest: PropTypes.bool,
	useStationDeliveryIdAsLoadingNumber: PropTypes.bool,
	carrierNeedsToProvidePickupWindow: PropTypes.bool,
	requireLoadingNumberPerStationOnSplitLoads: PropTypes.bool,
	useLoadingAssignments: PropTypes.bool,
	useAssignedLoadingNumbers: PropTypes.bool,
	stationId: PropTypes.number,
	lookupAssignedLoadingNumbers: PropTypes.func,
	backups: PropTypes.array,
	onChange: PropTypes.func,

	stationDeliveryId: PropTypes.number,
	classes: PropTypes.object,
};

export default _.flow([withStyles(styles)])(DeliveryTerminalSupplierPicker);
